import { useState } from 'react';
import { X, Mail, Building2 } from 'lucide-react';
import { API_ENDPOINTS } from '@/config/api';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ContactSalesModal({ isOpen, onClose }: ContactSalesModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.CONTACT_SALES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, company, teamSize, message }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setEmail('');
          setName('');
          setCompany('');
          setTeamSize('');
          setMessage('');
          onClose();
        }, 3000);
      } else {
        console.error('Failed to send enterprise inquiry:', result);
        alert(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error sending enterprise inquiry:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white dark:bg-[#0F1F3D] rounded-2xl shadow-2xl border border-gray-200 dark:border-[#1E3A8A]/30 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
        >
          <X size={24} />
        </button>

        {!submitted ? (
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#60A5FA] to-[#1E3A8A] rounded-full mb-4">
                <Building2 className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Contact Enterprise Sales
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Let's discuss how xRepl.ai Enterprise can transform your organization's communication.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Team Size
                </label>
                <select
                  id="teamSize"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white"
                >
                  <option value="">Select team size</option>
                  <option value="1-10">1-10 users</option>
                  <option value="11-50">11-50 users</option>
                  <option value="51-200">51-200 users</option>
                  <option value="201-500">201-500 users</option>
                  <option value="500+">500+ users</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tell us about your needs
                </label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0A1628] border border-gray-300 dark:border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                  placeholder="Any specific requirements or questions?"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#60A5FA] to-[#1E3A8A] text-white font-semibold rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    <span>Contact Sales</span>
                  </>
                )}
              </button>
            </form>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
              Our sales team will get back to you within 24 hours.
            </p>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Thank you!
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Our Enterprise sales team will reach out to you shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
